import * as React from "react";
import { Branch, Currency, XtrfService } from "gen/clients";
import Drawer from "@mui/material/Drawer";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { Form, Formik } from "formik";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import * as Yup from "yup";
import { DrawerActions } from "components/DrawerActions/DrawerActions";
import { SELECT_OPTION_COMPARATOR } from "../../../../../utils/stringUtils";

interface Props {
  branch: Branch;
  onClose: () => void;
  onSave: (branch: Branch) => void;
  existingBranches: Branch[];
  xtrfServices: XtrfService[];
}

interface FormValues {
  id: string;
  name: string;
  xtrfId: string;
  xtrfServiceId: string;
  currency: string;
  supportEmail: string;
  phoneNumber: string;
  websiteUrl: string;
}

const EditBranchPanel: React.FC<Props> = ({ branch, onClose, onSave, existingBranches, xtrfServices }) => {
  // Create validation schema based on whether branch already has an ID
  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        id: branch.id
          ? Yup.string().trim() // If branch has ID, it's disabled so no validation needed
          : Yup.string().required("Branch ID is required").trim(), // If no ID, it's required
        name: Yup.string().required("Branch name is required"),
        xtrfId: Yup.number()
          .typeError("XTRF ID must be a number")
          .required("XTRF ID is required")
          .positive("XTRF ID must be a positive number")
          .integer("XTRF ID must be an integer"),
        currency: Yup.string().required("Currency is required"),
        supportEmail: Yup.string().email("Invalid email address").notRequired(),
        phoneNumber: Yup.string().notRequired(),
        websiteUrl: Yup.string().url("Invalid URL format").notRequired()
      }),
    [branch.id]
  );
  const initialValues: FormValues = {
    id: branch.id,
    name: branch.name,
    xtrfId: branch.xtrfId.toString(),
    xtrfServiceId: branch.xtrfServiceId ? branch.xtrfServiceId.toString() : "",
    currency: branch.currency || "",
    supportEmail: branch.supportEmail || "",
    phoneNumber: branch.phoneNumber || "",
    websiteUrl: branch.websiteUrl || ""
  };

  const xtrfServiceOptions = React.useMemo(
    () => [
      { value: "", label: "None" },
      ...xtrfServices
        .filter(service => service.active)
        .map(service => ({
          value: service.id.toString(),
          label: service.name
        }))
        .sort(SELECT_OPTION_COMPARATOR)
    ],
    [xtrfServices]
  );

  const currencyOptions = React.useMemo(
    () => [
      { value: "", label: "Select currency" },
      { value: "USD", label: "USD - US Dollar" },
      { value: "EUR", label: "EUR - Euro" },
      { value: "GBP", label: "GBP - British Pound" }
    ],
    []
  );

  const handleSubmit = (values: FormValues) => {
    // If branch already has an ID, keep it. Otherwise, use the provided ID
    const branchId = branch.id || values.id.trim();

    onSave({
      id: branchId,
      name: values.name,
      xtrfId: Number(values.xtrfId),
      xtrfServiceId: values.xtrfServiceId ? Number(values.xtrfServiceId) : undefined,
      currency: values.currency as Currency,
      supportEmail: values.supportEmail.trim() || undefined,
      phoneNumber: values.phoneNumber.trim() || undefined,
      websiteUrl: values.websiteUrl.trim() || undefined
    });
  };

  const validate = (values: FormValues) => {
    const errors: Partial<FormValues> = {};

    // Check for duplicate Branch ID (only if the field is editable and has changed)
    if (!branch.id && values.id.trim() && existingBranches.some(b => b.id === values.id.trim())) {
      errors.id = "A branch with this ID already exists";
    }

    // Check for duplicate XTRF ID (excluding the current branch)
    if (
      values.xtrfId &&
      Number(values.xtrfId) !== branch.xtrfId &&
      existingBranches.some(b => b.xtrfId === Number(values.xtrfId))
    ) {
      errors.xtrfId = "A branch with this XTRF ID already exists";
    }

    // Check for duplicate name (excluding the current branch)
    if (values.name && values.name !== branch.name && existingBranches.some(b => b.name === values.name)) {
      errors.name = "A branch with this name already exists";
    }

    return errors;
  };

  return (
    <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 500 } }}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validate={validate}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <DrawerHeader title="Edit Branch" onClose={onClose} />
            <DrawerBody>
              <Stack spacing={3}>
                <TextInputField
                  name="id"
                  label="Branch ID"
                  required
                  fullWidth
                  disabled={!!branch.id}
                  placeholder={branch.id ? undefined : "Enter a unique branch identifier"}
                  helperText={
                    branch.id
                      ? "Branch ID cannot be changed once set"
                      : "Required: Provide a unique identifier for this branch"
                  }
                />
                <TextInputField name="name" label="Branch Name" required fullWidth autoFocus />
                <TextInputField name="xtrfId" label="XTRF ID" required fullWidth type="number" />
                <SelectOneField name="currency" label="Currency" required fullWidth options={currencyOptions} />
                <SelectOneField
                  name="xtrfServiceId"
                  label="Default XTRF Service"
                  fullWidth
                  options={xtrfServiceOptions}
                />
                <TextInputField
                  name="supportEmail"
                  label="Customer Support Email Address"
                  fullWidth
                  type="email"
                  placeholder="Enter customer support email address"
                />
                <TextInputField name="phoneNumber" label="Phone Number" fullWidth placeholder="Enter phone number" />
                <TextInputField
                  name="websiteUrl"
                  label="Website URL"
                  fullWidth
                  type="url"
                  placeholder="Enter website URL (e.g., https://example.com)"
                />
              </Stack>
            </DrawerBody>
            <DrawerActions>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isSubmitting}
                disabled={!isValid || !dirty}
              >
                Save
              </LoadingButton>
              <Button onClick={onClose}>Cancel</Button>
            </DrawerActions>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export { EditBranchPanel };
